import { SlDropdown } from "@shoelace-style/shoelace";
import "@shoelace-style/shoelace/dist/components/button-group/button-group.js";
import "@shoelace-style/shoelace/dist/components/button/button.js";
import "@shoelace-style/shoelace/dist/components/dropdown/dropdown.js";
import "@shoelace-style/shoelace/dist/components/icon/icon.js";
import "@shoelace-style/shoelace/dist/components/menu-item/menu-item.js";
import "@shoelace-style/shoelace/dist/components/menu/menu.js";
import "@shoelace-style/shoelace/dist/themes/dark.css";
import "@shoelace-style/shoelace/dist/themes/light.css";
import { registerIconLibrary } from "@shoelace-style/shoelace/dist/utilities/icon-library.js";

import { LitElement, PropertyValues, css, html, nothing, unsafeCSS } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import { classMap } from "lit/directives/class-map.js";
import { Ref, createRef, ref } from "lit/directives/ref.js";
import { repeat } from "lit/directives/repeat.js";

import shoelaceStekzTheme from "@/wc/text-editor/styles/shoelace-stekz-theme.scss?inline";

import { MenuItemState } from ".";

registerIconLibrary("component-assets", {
    resolver: (name) => `/assets/text-editor/menu-bar/${name}.svg`,
});

registerIconLibrary("primeicons", {
    resolver: (name) => {
        const shortName = name.replace("pi-", "");
        return `/assets/primeicons/${shortName}.svg`;
    },
    mutator: (svg) => {
        // Scale the icon up, so it's the same size as others.
        svg.style.width = "auto";
        svg.style.height = "1.4rem";
        svg.style.margin = "-0.2rem";
        svg.setAttribute("width", "16");
        svg.setAttribute("height", "16");
        svg.setAttribute("fill", "currentColor");
    },
});

/**
 * Returns the menu items sorted based on the order property.
 */
const sortItems = <T extends { order?: number }>(items: T[] = []): T[] => {
    return items.toSorted((a, b) => {
        return (
            (a.order ?? Number.MAX_SAFE_INTEGER) -
            (b.order ?? Number.MAX_SAFE_INTEGER)
        );
    });
};

const iconTemplate = ({
    name,
    library = "component-assets",
    slot = "prefix",
}: {
    name: string;
    library?: string;
    slot?: string;
}) => html` <sl-icon name=${name} library=${library} slot=${slot}></sl-icon>`;

const itemTemplates = {
    toggle: (item: MenuItemState) =>
        html`<sl-tooltip placement="top" content=${item.label}>
            <sl-button
                class=${classMap({
                    "stekz-button": true,
                    "pressed": item.active,
                })}
                ?caret=${item?.caret}
                ?disabled=${item?.disabled}
                @click=${(e: MouseEvent) => {
                    item.run?.(e);
                }}
            >
                ${item?.icon ? iconTemplate(item.icon) : nothing}
                ${item?.showLabel ? item?.label : nothing}
            </sl-button>
        </sl-tooltip>`,
    dropdown: (item: MenuItemState) =>
        html`<sl-tooltip placement="top" content=${item.label}>
            <c-menu-bar-dropdown .item=${item}></c-menu-bar-dropdown>
        </sl-tooltip>`,
};

@customElement("c-menu-bar-dropdown")
class _StekzMenuBarDropdown extends LitElement {
    @property()
    item: MenuItemState;

    dropdown: Ref<SlDropdown> = createRef();

    @state()
    _subItems: MenuItemState["items"];

    @state()
    _selectedPath = "";

    static styles = css`
        sl-menu-item::part(base) {
            color: var(--text-color);
        }

        sl-menu-item::part(label) {
            color: inherit;
        }

        sl-menu-item:hover::part(base) {
            background: var(--stekz-build-background);
            color: var(--text-color);
        }

        sl-menu-item:focus::part(base),
        sl-menu-item:focus-visible::part(base) {
            background: var(--sl-color-primary-100);
            color: var(--text-color);
        }

        sl-menu-item[checked]::part(base) {
            background: var(--sl-color-primary-100);
            color: var(--text-color);
        }

        sl-menu-item.selected::part(base) {
            background: var(--stekz-build-background);
            color: var(--text-color);
        }
    `;

    private async resolveItems(items: MenuItemState["items"] = []) {
        return Promise.all(
            sortItems(items).map(async (item) => {
                const nextItems = item.getItems
                    ? await item.getItems()
                    : item.items || [];

                return {
                    ...item,
                    items:
                        item.type === "dropdown"
                            ? await this.resolveItems(nextItems)
                            : undefined,
                } as MenuItemState;
            })
        );
    }

    async willUpdate(changedProperties: PropertyValues<this>) {
        if (changedProperties.has("item")) {
            if (this.item?.getItems) {
                this._subItems = await this.resolveItems(
                    await this.item.getItems()
                );
            } else if (this.item?.items?.length) {
                this._subItems = await this.resolveItems(this.item.items);
            } else {
                this._subItems = [];
            }
        }
    }

    private renderMenuItem(menuItem: MenuItemState, path: string) {
        const isDropdown = menuItem.type === "dropdown";

        return html`<sl-menu-item
            class=${classMap({
                selected: this._selectedPath === path,
            })}
            type=${menuItem?.active ? "checkbox" : "normal"}
            ?checked=${menuItem?.active}
            ?disabled=${menuItem?.disabled && !menuItem?.active}
            @focus=${() => {
                this._selectedPath = path;
            }}
            @mouseenter=${() => {
                this._selectedPath = path;
            }}
            @click=${(e: MouseEvent) => {
                if (isDropdown && !menuItem?.run) {
                    return;
                }
                e.preventDefault();
                e.stopPropagation();
                this.dropdown.value?.hide?.();
                menuItem?.run?.(e);
            }}
        >
            ${menuItem?.icon ? iconTemplate(menuItem.icon) : nothing}
            ${menuItem?.label || ""}
            ${
                isDropdown
                    ? html`
                          <sl-menu slot="submenu">
                              ${sortItems(menuItem.items || []).map(
                                  (subItem, index) =>
                                      this.renderMenuItem(
                                          subItem,
                                          `${path}/${index}-${subItem.name}`
                                      )
                              )}
                          </sl-menu>
                      `
                    : nothing
            }
        </sl-menu-item>`;
    }

    render() {
        return html`<sl-dropdown ${ref(this.dropdown)}>
            <sl-button
                class="stekz-button"
                slot="trigger"
                ?caret=${this.item?.caret}
                ?disabled=${this.item?.disabled}
            >
                ${this.item?.icon ? iconTemplate(this.item.icon) : nothing}
                ${this.item?.showLabel ? this.item?.label : nothing}
            </sl-button>
            <sl-menu>
                ${
                    this._subItems
                        ? sortItems(this._subItems).map((menuItem, index) =>
                              this.renderMenuItem(
                                  menuItem,
                                  `${index}-${menuItem.name}`
                              )
                          )
                        : html`<div>Loading...</div>`
                }
            </sl-menu>
        </sl-dropdown>`;
    }
}

@customElement("c-menu-bar")
export class StekzMenuBar extends LitElement {
    @property({ type: Array, attribute: false })
    items?: Array<MenuItemState> = [];

    _itemsPerGroup: Record<number, Array<MenuItemState>> = [];

    async willUpdate(changedProperties: PropertyValues<this>) {
        if (changedProperties.has("items")) {
            this._itemsPerGroup = Object.groupBy(
                sortItems(this.items),
                (item) => item.group
            );
        }
    }

    static styles = [
        unsafeCSS(shoelaceStekzTheme),
        css`
            /* Shoelace button does not define a "pressed" state by default, so we define
                a class to style a button as pressed. */
            .stekz-button.pressed:is([disabled], :not(:active))::part(base) {
                background: var(--stekz-build-background);
                border-color: var(--sl-color-neutral-400);
            }

            .stekz-button::part(base):focus-visible {
                box-shadow: 0 0 0 2px var(--sl-color-primary-400);
            }

            .stekz-button[disabled]::part(base) {
                cursor: default;
            }

            /* Overrides sl-button-group's default nowrap so buttons reflow instead
                of getting clipped when the toolbar narrows. */
            sl-button-group::part(base) {
                flex-wrap: wrap;
            }
        `,
    ];

    render() {
        return repeat(
            Object.entries(this._itemsPerGroup),
            ([group, _]) => group,
            ([group, items]) => {
                return html`<sl-button-group group=${group}>
                    ${repeat(
                        items,
                        (item) => item.name,
                        (item) => itemTemplates?.[item.type](item)
                    )}
                </sl-button-group>`;
            }
        );
    }
}

declare global {
    interface HTMLElementTagNameMap {
        "c-menu-bar": StekzMenuBar;
    }
}
